import * as THREE from 'three';
import { LocalizationState, type NavMeConfig, type Vec3 } from '../types';
import { MultisetRestClient } from './multisetRestClient';
import { intrinsicsFromTrackedCamera } from './cameraCapture';
import { logVpsResult, logEvent, recordLocSample } from './debugLog';
import { t } from '../i18n';
import {
  loadEightWallEngine,
  loadEightWallExtras,
  isEightWallLikelySupported,
  type XR8Global,
  type XR8PipelineModule,
  type XRExtrasGlobal,
} from './eightWallLoader';
import { withTimeout } from '../utils/math';

/**
 * iOS Safari primary path: 8th Wall's free (as of Feb 2026, see README)
 * Engine Binary provides continuous 6-DOF SLAM camera tracking in stock
 * Safari — no WebXR involved. We drive its three.js integration
 * (XR8.Threejs) to get a live-tracked THREE.PerspectiveCamera, then use
 * MultiSet's REST API (multisetRestClient.ts, already used by the lighter
 * cameraArSession.ts fallback) for periodic absolute position fixes to
 * compute the map<->world alignment — the same worldFromMap contract
 * PoseTransformService already expects from the WebXR path, so main.ts can
 * reuse the exact same NavigationOverlay/PoseTransformService/onXRFrame
 * code for both modes (see main.ts's NavContext).
 *
 * Coordinate alignment math: 8th Wall's tracked camera has a world
 * transform in ITS OWN arbitrary tracking-space origin (worldFromCamera =
 * camera.matrixWorld). MultiSet's REST response gives that same physical
 * camera's pose in MAP space (mapFromCamera, built from position+rotation
 * with isRightHanded:true to match this project's Three.js/RHS
 * convention). Composing worldFromCamera * mapFromCamera^-1 gives
 * worldFromMap — a standard "align two frames via one shared pose"
 * computation, not an invented API; both inputs come from confirmed,
 * documented sources (XR8.Threejs.xrScene().camera and the MultiSet REST
 * response).
 */

export interface EightWallSceneHandles {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
}

export interface EightWallSessionCallbacks {
  onState: (state: LocalizationState, detail?: string) => void;
  /** Fired once, when 8th Wall's three.js scene/camera/renderer become available. */
  onSceneReady: (handles: EightWallSceneHandles) => void;
  /** Fired on every successful MultiSet fix, mirrors NavMeARSession's role. */
  onLocalized: (position: Vec3, worldFromMap: THREE.Matrix4, confidence?: number) => void;
  /** Fired every camera frame (8th Wall's onUpdate lifecycle hook). */
  onFrame: () => void;
  onSessionStart: () => void;
  onSessionEnd: () => void;
}

// VPS query cadence. REVERTED 2500 → 4000 (Test 20): the faster cadence
// BACKFIRED — more/bigger corrections (6 eased + 2 held incl. Δ4.56 m/31° in
// ~23 s) and min FPS fell to 18.6 (added screenshot/REST load). More frequent
// VPS multiplies drift events and hurts smoothness; it does NOT shrink the
// per-fix ~1.6 m SLAM error.
const LOCALIZE_INTERVAL_MS = 4000;
const MAX_QUERY_DIMENSION = 1280; // matches MultiSet's REST limit exactly
/**
 * Minimum VPS confidence to apply a result — two-tier:
 * - FIRST fix: lenient (0.28). Debug logs show session-start confidences of
 *   0.23-0.36 that climb once tracking warms up; gating the first fix at
 *   0.45 can block localization entirely. A weak first fix is safe: there's
 *   no established anchor to corrupt, and later good results correct it
 *   (via the pose-transform confirmation logic).
 * - ESTABLISHED anchor: strict (0.45). Low-conf results teleported the
 *   anchor 5-8 m in testing; the REST path (unlike the WebXR SDK's
 *   confidenceCheck) had no filter at all.
 */
const MIN_CONFIDENCE_FIRST_FIX = 0.28;
const MIN_CONFIDENCE_ESTABLISHED = 0.45;

export class EightWallSession {
  private restClient: MultisetRestClient;
  private cb: EightWallSessionCallbacks;
  private canvas: HTMLCanvasElement;

  private xr8: XR8Global | null = null;
  private xrExtras: XRExtrasGlobal | null = null;
  private trackedCamera: THREE.PerspectiveCamera | null = null;
  private active = false;
  private inFlight = false;
  private hasLocalizedOnce = false;
  private intervalId: number | null = null;
  /** Consecutive VPS failures — single misses (motion blur) are normal and shouldn't flash 'Relocalizing' at the user. */
  private consecutiveFailures = 0;
  /** Fast retries burned on the "camera feed not attached" startup race. */
  private feedRetries = 0;

  static async isSupported(): Promise<boolean> {
    return isEightWallLikelySupported();
  }

  constructor(config: NavMeConfig, handles: { canvas: HTMLCanvasElement }, callbacks: EightWallSessionCallbacks) {
    this.restClient = new MultisetRestClient(config);
    this.canvas = handles.canvas;
    this.cb = callbacks;
  }

  /**
   * Validates MultiSet credentials AND preloads the 8th Wall script — both
   * before the user gesture, same reason NavMeARSession pre-authorizes.
   * Each step gets its own timeout + distinct error message so a hung
   * network request or a blocked CDN script shows up as a clear, specific
   * failure instead of an indefinite "Connecting to MultiSet…" freeze.
   */
  async authorize(): Promise<void> {
    try {
      this.cb.onState(LocalizationState.NotStarted, 'Loading AR engine…');
      await withTimeout(
        loadEightWallEngine(),
        20_000,
        '8th Wall engine script did not load within 20s — check that cdn.jsdelivr.net is reachable (no ad-blocker/CSP/firewall blocking it) and retry.',
      );
      this.xr8 = window.XR8 ?? null;
      if (!this.xr8) throw new Error('8th Wall script loaded but window.XR8 is missing — engine may have failed to initialize.');

      // XRExtras.FullWindowCanvas is 8th Wall's own fix for the camera
      // canvas not filling the screen (see eightWallLoader.ts for why this
      // is needed beyond plain CSS). Non-fatal if it fails to load — camera
      // mode still works, just without the full-screen-resize guarantee —
      // so this doesn't get a hard withTimeout() failure path.
      try {
        this.xrExtras = await withTimeout(loadEightWallExtras(), 10_000, 'xrextras timed out');
      } catch {
        this.xrExtras = null;
      }

      this.cb.onState(LocalizationState.NotStarted, 'Authenticating with MultiSet…');
      await withTimeout(
        this.restClient.authorize(),
        15_000,
        'MultiSet authentication did not respond within 15s — check your network connection and that this origin is whitelisted (dashboard → Credentials → Settings → Domains).',
      );
    } catch (err) {
      this.cb.onState(LocalizationState.Error, authErrorMessage(err));
      throw err;
    }
  }

  /** Registers pipeline modules. Safe to call outside a user gesture — XR8.run() (in startSession) is the actual camera/permission trigger. */
  initialize(): void {
    if (!this.xr8) throw new Error('authorize() must be called first');

    // 8th Wall's XR8.Threejs.pipelineModule() looks for Three.js as a
    // GLOBAL `window.THREE` (its examples assume a plain <script> tag
    // build, not an ES-module bundler). This project imports three.js as
    // a module (`import * as THREE from 'three'`), so it's scoped to this
    // bundle and never lands on `window` unless we put it there ourselves.
    // Confirmed root cause of "window.THREE does not exist but is required
    // by the ThreeJS pipeline module" on real-device testing (2026-07-09).
    (window as unknown as { THREE?: typeof THREE }).THREE = THREE;

    const customModule: XR8PipelineModule = {
      name: 'navme-scene',
      onStart: () => {
        const { scene, camera, renderer } = this.xr8!.Threejs.xrScene();
        // #35 REVERTED (Tests 16–17): 8th Wall's default pixel ratio here is
        // already 1, and setting it either way (1.5 → 30 fps, 0.7 → 30 fps) is
        // WORSE than leaving the default (38). Resolution is not a usable lever
        // via setPixelRatio — 8th Wall manages the buffer — so don't touch it.
        this.trackedCamera = camera;
        this.cb.onSceneReady({ scene, camera, renderer });
        this.cb.onSessionStart();
        this.cb.onState(LocalizationState.Localizing);
        void this.runLocalization();
        this.intervalId = window.setInterval(() => void this.runLocalization(), LOCALIZE_INTERVAL_MS);
      },
      onUpdate: () => this.cb.onFrame(),
      onException: (error: unknown) => this.cb.onState(LocalizationState.Error, String(error)),
      onCameraStatusChange: (args) => {
        if (args.status === 'failed') {
          this.cb.onState(LocalizationState.PermissionNeeded, 'Camera permission was denied');
        }
      },
    };

    // XR8.CanvasScreenshot's default maxDimension (1280) already matches
    // MultiSet's REST limit, but set it explicitly so that stays true even
    // if 8th Wall changes its own default in a future version.
    const cs = (this.xr8 as unknown as { CanvasScreenshot?: { configure: (o: { maxDimension: number; jpgCompression: number }) => void; pipelineModule: () => XR8PipelineModule } }).CanvasScreenshot;
    cs?.configure({ maxDimension: MAX_QUERY_DIMENSION, jpgCompression: 85 });

    const modules = [this.xr8.GlTextureRenderer.pipelineModule()];
    // Must come right after GlTextureRenderer, before Threejs — matches the
    // order in 8th Wall's own "Hello World" sample (see eightWallLoader.ts
    // comment). Keeps the canvas's WebGL drawing buffer synced to its CSS
    // layout size across the camera-feed setup and orientation changes.
    if (this.xrExtras) modules.push(this.xrExtras.FullWindowCanvas.pipelineModule());
    modules.push(this.xr8.Threejs.pipelineModule(), this.xr8.XrController.pipelineModule(), customModule);
    if (cs) modules.push(cs.pipelineModule());

    this.xr8.addCameraPipelineModules(modules);
  }

  /** MUST be the first call inside a click/tap handler — triggers XR8.run(), which requests camera access. */
  startSession(): Promise<void> {
    if (!this.xr8) throw new Error('initialize() must be called first');
    this.active = true;
    this.hasLocalizedOnce = false;
    this.feedRetries = 0;
    this.xr8.run({ canvas: this.canvas, allowedDevices: this.xr8.XrConfig.device().ANY });
    return Promise.resolve();
  }

  async relocalize(): Promise<void> {
    if (!this.active) return;
    this.cb.onState(LocalizationState.Relocalizing);
    await this.runLocalization();
  }

  stopSession(): void {
    this.active = false;
    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.xr8?.stop?.();
    this.cb.onState(LocalizationState.NotStarted);
    this.cb.onSessionEnd();
  }

  isActive(): boolean {
    return this.active;
  }

  dispose(): void {
    this.stopSession();
  }

  private async runLocalization(): Promise<void> {
    if (!this.active || this.inFlight || !this.trackedCamera) return;
    const cs = (this.xr8 as unknown as { CanvasScreenshot?: { takeScreenshot: () => Promise<string> } })?.CanvasScreenshot;
    if (!cs) {
      this.cb.onState(LocalizationState.Error, 'CanvasScreenshot module unavailable');
      return;
    }

    this.inFlight = true;
    const t0 = performance.now();
    try {
      const base64 = await cs.takeScreenshot();
      // Downscale to MultiSet's 1280px max BEFORE querying. CanvasScreenshot
      // returns the canvas at native device resolution (iPhone: ~1170x2532 at
      // dpr 3) — oversized images cause silent "no pose found" failures. The
      // getUserMedia path (cameraCapture.captureFrame) already did this; the
      // 8th Wall path was missing it.
      const { dataUrl, width, height } = await downscaleJpeg(base64, 1280);
      // Real device-measured intrinsics from 8th Wall's live-tracked camera,
      // computed against the DOWNSCALED dimensions (fx/fy derive from FOV +
      // pixel size, so they must match the image actually sent).
      const intrinsics = intrinsicsFromTrackedCamera(this.trackedCamera, width, height);

      const result = await this.restClient.queryMap(
        dataUrl,
        intrinsics,
        { width, height },
        { isRightHanded: true },
      );
      // Full attempt duration: screenshot capture + downscale + VPS round
      // trip. This is the number to look at when "localization feels slow"
      // — also in the debug report ('vps' lines + 'vps t' summary).
      const attemptMs = Math.round(performance.now() - t0);
      // Throttle-free single log per attempt — remote-inspect Safari/Chrome
      // to see the raw VPS response while diagnosing localization issues.
      console.info('[NavMe] VPS query', { attemptMs, width, height, intrinsics, result });

      const minConf = this.hasLocalizedOnce
        ? MIN_CONFIDENCE_ESTABLISHED
        : MIN_CONFIDENCE_FIRST_FIX;
      if (result.poseFound && result.confidence !== undefined && result.confidence < minConf) {
        // Pose found but too uncertain to trust — applying these caused
        // multi-metre anchor teleports (see debug log analysis).
        this.consecutiveFailures++;
        logVpsResult(false, result.confidence, 'below confidence threshold', attemptMs);
        if (!this.hasLocalizedOnce || this.consecutiveFailures >= 3) {
          this.cb.onState(
            this.hasLocalizedOnce ? LocalizationState.Relocalizing : LocalizationState.Localizing,
            t('loc.lowConf', { c: result.confidence.toFixed(2) }),
          );
        }
      } else if (result.poseFound && result.position && result.rotation) {
        this.consecutiveFailures = 0;
        // Time-to-first-fix is recorded mode-agnostically in main.ts's
        // onLocalized via debugLog.markLocalized().
        this.hasLocalizedOnce = true;
        logVpsResult(true, result.confidence, undefined, attemptMs);
        // Heatmap sample: where in the map localization succeeded, and how well.
        recordLocSample({ ...result.position, c: result.confidence ?? 0.5 });
        const worldFromMap = computeWorldFromMap(this.trackedCamera, result.position, result.rotation);
        this.cb.onState(LocalizationState.Localized);
        this.cb.onLocalized(result.position, worldFromMap, result.confidence);
      } else {
        this.consecutiveFailures++;
        logVpsResult(false, result.confidence, 'no pose', attemptMs);
        // Single misses are expected while walking (motion blur) — stay
        // quiet until failures persist, so the status doesn't flicker.
        if (!this.hasLocalizedOnce || this.consecutiveFailures >= 3) {
          const conf = result.confidence !== undefined ? ` (confidence ${result.confidence.toFixed(2)})` : '';
          this.cb.onState(
            this.hasLocalizedOnce ? LocalizationState.Relocalizing : LocalizationState.Localizing,
            `${t('loc.noPose')}${conf}`,
          );
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      logEvent('error', `vps query threw: ${msg}`);
      // Transient startup race: the first attempt can fire before 8th Wall's
      // camera feed is attached. Waiting for the next 4 s interval cost ~5 s
      // of time-to-first-fix on every session (confirmed in debug report
      // 2026-07-11) — retry quickly instead, capped to avoid a hot loop.
      if (/not attached to a running camera feed/i.test(msg)) {
        if (this.active && this.feedRetries < 5) {
          this.feedRetries++;
          window.setTimeout(() => void this.runLocalization(), 750);
        }
        return;
      }
      this.cb.onState(
        this.hasLocalizedOnce ? LocalizationState.Relocalizing : LocalizationState.Error,
        msg,
      );
    } finally {
      this.inFlight = false;
    }
  }
}

/** worldFromMap = worldFromCamera * mapFromCamera^-1 — see file header comment. */
function computeWorldFromMap(
  trackedCamera: THREE.PerspectiveCamera,
  position: { x: number; y: number; z: number },
  rotation: { x: number; y: number; z: number; w: number },
): THREE.Matrix4 {
  trackedCamera.updateMatrixWorld(true);
  const worldFromCamera = trackedCamera.matrixWorld.clone();

  const mapFromCamera = new THREE.Matrix4().compose(
    new THREE.Vector3(position.x, position.y, position.z),
    new THREE.Quaternion(rotation.x, rotation.y, rotation.z, rotation.w),
    new THREE.Vector3(1, 1, 1),
  );

  return worldFromCamera.multiply(mapFromCamera.invert());
}

/**
 * Decodes a base64 JPEG and downscales it so its longest side is <= maxDim
 * (MultiSet REST query limit). Returns the (possibly re-encoded) data URL
 * plus final pixel dimensions. No-op re-encode is skipped when already small.
 */
async function downscaleJpeg(
  base64: string,
  maxDim: number,
): Promise<{ dataUrl: string; width: number; height: number }> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('Could not decode screenshot'));
    el.src = `data:image/jpeg;base64,${base64}`;
  });

  const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
  const width = Math.round(img.naturalWidth * scale);
  const height = Math.round(img.naturalHeight * scale);
  if (scale >= 1) {
    return { dataUrl: `data:image/jpeg;base64,${base64}`, width, height };
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.drawImage(img, 0, 0, width, height);
  return { dataUrl: canvas.toDataURL('image/jpeg', 0.85), width, height };
}

function authErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (/401|unauthor/i.test(msg)) return 'Invalid Client ID or Secret (401)';
  if (/cors|fetch/i.test(msg))
    return 'Request blocked — is this origin whitelisted in the MultiSet dashboard (Credentials → Settings → Domains)?';
  return msg;
}
